/**
 * GOSI contributor reconciliation — template download, workbook upload, and
 * the comparison job it produces.
 *
 *   GET  /api/v1/tenants/me/insurance-enrollments/template                payroll:read
 *   POST /api/v1/tenants/me/insurance-enrollments/reconciliation          payroll:run
 *   GET  /api/v1/tenants/me/insurance-enrollments/reconciliation/{jobId}  payroll:read
 *
 * Two things this module refuses to let the UI get wrong:
 *
 * 1. `processingStatus: COMPLETED` means the COMPARISON finished, never that
 *    anything was reconciled. `reconciliationPhase()` names it `"ready"` so no
 *    consumer can accidentally render it as a success.
 * 2. A 422 is not a one-line error. The server reports EVERY safely reportable
 *    issue in one pass and answers `reExportRequired` itself, so the admin can
 *    fix everything before uploading again. `parseFileRejection()` lifts that
 *    whole payload off the thrown ApiError instead of showing `title` alone.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { useTenantQuery, useTenantMutation } from "./factory";
import { useApiClient } from "../react/ApiProvider";
import { qk } from "../query-keys/qk";
import type { ApiError } from "../errors/ApiError";
import type { ApiClient } from "../client";
import type { components } from "../generated/schema";
import { triggerBlobDownload } from "./bulk-import";

export type ReconciliationJobView = components["schemas"]["ReconciliationJobView"];
export type ReconciliationCounts = components["schemas"]["ReconciliationCounts"];
export type ReconciliationCoverage = components["schemas"]["ReconciliationCoverage"];
export type ReconciliationRejectionArgs = components["schemas"]["ReconciliationRejectionArgs"];
export type ReconciliationFileIssue = components["schemas"]["ReconciliationFileIssue"];
export type ReconciliationProcessingStatus = ReconciliationJobView["processingStatus"];
export type ReconciliationRowView = components["schemas"]["ReconciliationRowView"];
export type ReconciliationFieldDiffView = components["schemas"]["ReconciliationFieldDiffView"];
export type PagedReconciliationRows = components["schemas"]["PagedReconciliationRows"];
export type ReconciliationClassification = ReconciliationRowView["classification"];
export type ReconciliationFieldCode = ReconciliationFieldDiffView["fieldCode"];
export type ReconciliationMateriality = ReconciliationFieldDiffView["materiality"];
export type ReconciliationRowBlockedReason = ReconciliationRowView["blockedReasons"][number];

/* CC-408 — the accept flow, the open-review list, and per-difference state. */
export type AcceptDecisionItem = components["schemas"]["AcceptDecisionItem"];
export type AcceptDecision = AcceptDecisionItem["decision"];
export type AcceptPreflightResponse = components["schemas"]["AcceptPreflightResponse"];
export type PreflightRowResult = components["schemas"]["PreflightRowResult"];
export type AcceptReconciliationResponse = components["schemas"]["AcceptReconciliationResponse"];
export type AcceptedRowResult = components["schemas"]["AcceptedRowResult"];
export type AcceptanceDecisionView = components["schemas"]["AcceptanceDecisionView"];
export type ReconciliationJobSummary = components["schemas"]["ReconciliationJobSummary"];
export type ReconciliationJobPage = components["schemas"]["ReconciliationJobPage"];
export type ReconciliationReviewStatus = NonNullable<ReconciliationJobSummary["reviewStatus"]>;

/**
 * The enrollment field catalog, closed at 14 (be-730). Every one carries copy in
 * both languages; the raw code never reaches a screen.
 */
export const RECONCILIATION_FIELD_CODES = [
  "subscriberKind",
  "pensionRegime",
  "coveragePension",
  "coverageSaned",
  "coverageHazard",
  "coverageMaternity",
  "costBearing",
  "gosiWageOverride",
  "registrationNo",
  "participationDate",
  "establishmentId",
  "evidenceRef",
  "effectiveFrom",
  "effectiveTo",
] as const satisfies readonly ReconciliationFieldCode[];

/**
 * A reconciliation ROW's blockers. Four values — deliberately NOT the readiness
 * list's five: a row's review cursor joins the enrollment, so an employee whose
 * windows have all lapsed reads `NO_ENROLLMENT` here where the readiness radar
 * says `NO_CURRENT_WINDOW`. The same employee legitimately shows a different
 * code on each screen and neither is wrong, so the two never share a table.
 */
export const RECONCILIATION_ROW_BLOCKED_REASONS = [
  "NO_ENROLLMENT",
  "SUGGESTED_ONLY",
  "NOT_VERIFIED",
  "PENSION_REGIME_UNKNOWN",
] as const satisfies readonly ReconciliationRowBlockedReason[];

/**
 * One refusal vocabulary serves both the 422 and a FAILED job — the contract
 * says so explicitly, so the copy table is shared too.
 */
export const RECONCILIATION_REJECTION_REASONS = [
  "FILE_UNREADABLE",
  "FILE_TOO_LARGE",
  "MACROS_NOT_ALLOWED",
  "EXTERNAL_LINKS_NOT_ALLOWED",
  "HIDDEN_ROW_NOT_ALLOWED",
  "UNEXPECTED_SHEET",
  "SCHEMA_MISMATCH",
  "MANIFEST_INVALID",
  "UNSUPPORTED_SCHEMA_VERSION",
] as const;

export const RECONCILIATION_FAILURE_CLASSES = [
  "UNREADABLE",
  "CONTENT",
  "STRUCTURE",
  "INTEGRITY",
] as const;

export const RECONCILIATION_PROCESSING_STATUSES = [
  "QUEUED",
  "RUNNING",
  "COMPLETED",
  "FAILED",
] as const;

/**
 * Where the REVIEW stands, which is a different question from whether the
 * comparison parsed. Both statuses are on the job and only one of them answers
 * "has anybody decided anything" — rendering the processing status alone is how
 * a review with two employees already committed goes on saying nothing has
 * changed yet.
 */
export const RECONCILIATION_REVIEW_STATUSES = [
  "OPEN",
  "PARTIALLY_APPLIED",
  "APPLIED",
  "SUPERSEDED",
] as const satisfies readonly ReconciliationReviewStatus[];

const REVIEW_STATUS_SET = new Set<string>(RECONCILIATION_REVIEW_STATUSES);

type RejectionReason = (typeof RECONCILIATION_REJECTION_REASONS)[number];
type FailureClass = (typeof RECONCILIATION_FAILURE_CLASSES)[number];

const REASON_SET = new Set<string>(RECONCILIATION_REJECTION_REASONS);
const CLASS_SET = new Set<string>(RECONCILIATION_FAILURE_CLASSES);

/* ── Job phase ───────────────────────────────────────────────────────────── */

/**
 * What the admin is looking at, as one word.
 *
 * `"ready"` — not `"completed"` — is deliberate: the parse finished and a
 * comparison exists to review. Nothing has been reconciled at that point.
 * `"stalled"` is ours, not the server's: a job that has been non-terminal past
 * the ceiling stops being polled and gets a manual refresh instead of an
 * animation that runs until the tab closes.
 */
export type ReconciliationPhase = "queued" | "running" | "stalled" | "failed" | "ready";

/** How long a job may stay QUEUED/RUNNING before we stop polling it. */
export const RECONCILIATION_POLL_CEILING_MS = 5 * 60_000;

const POLL_INTERVAL_MS = 2000;

/**
 * @param polls how many times this job has been read. Only consulted when the
 *   job's own clock is unreadable — see below.
 */
export function reconciliationPhase(
  job: ReconciliationJobView | undefined,
  now: number = Date.now(),
  polls = 0,
): ReconciliationPhase {
  if (job === undefined) return "queued";
  // Terminal first, and unconditionally: a job that FAILED must report the
  // failure, never get masked by the ceiling because it also happens to be old.
  if (job.processingStatus === "FAILED") return "failed";
  if (job.processingStatus === "COMPLETED") return "ready";

  // `uploadedAt` is the job's own clock and normally answers "how long has this
  // been running". When it is absent or unreadable, elapsed time is UNKNOWN —
  // and unknown must not mean "poll forever", so we fall back to counting our
  // own reads. Either way the ceiling fires.
  const uploadedAt = Date.parse(job.uploadedAt ?? "");
  const elapsed = Number.isNaN(uploadedAt) ? polls * POLL_INTERVAL_MS : now - uploadedAt;
  if (elapsed > RECONCILIATION_POLL_CEILING_MS) return "stalled";

  // `reviewStatus` is deliberately not read here: it is null until parsing
  // completes, and null is not a terminal state — the parse is.
  return job.processingStatus === "RUNNING" ? "running" : "queued";
}

/** True when the job will never change again on its own. */
export function isReconciliationTerminal(phase: ReconciliationPhase): boolean {
  return phase === "failed" || phase === "ready" || phase === "stalled";
}

/**
 * Where the review stands, or `null` when there is no honest answer yet.
 *
 * `null` has two causes and both take the same treatment — say nothing about
 * decisions rather than guess at them:
 *   - the parse has not completed, so the server has not opened a review;
 *   - the server named a status this release does not know, and inventing
 *     "nothing decided yet" for it would be the exact lie this helper exists to
 *     prevent.
 */
export function reconciliationReviewStatus(
  job: { reviewStatus?: string | null } | null | undefined,
): ReconciliationReviewStatus | null {
  const status = job?.reviewStatus;
  if (typeof status !== "string" || !REVIEW_STATUS_SET.has(status)) return null;
  return status as ReconciliationReviewStatus;
}

/**
 * The next poll delay, or `false` to stop. Every way this query can stop
 * polling lives here so it can be reasoned about — and tested — in one place.
 */
export function reconciliationPollInterval(
  state: {
    status: "pending" | "error" | "success";
    data: ReconciliationJobView | undefined;
    dataUpdateCount: number;
  },
  now: number = Date.now(),
): number | false {
  // A 403, a 404 or a deleted job is permanent. The provider's retry policy
  // never retries 4xx, so an error state here is settled: asking again every
  // two seconds for as long as the tab is open cannot change the answer.
  if (state.status === "error") return false;
  // First read still in flight — nothing to decide on yet.
  if (state.data === undefined) return POLL_INTERVAL_MS;
  const phase = reconciliationPhase(state.data, now, state.dataUpdateCount);
  return isReconciliationTerminal(phase) ? false : POLL_INTERVAL_MS;
}

/* ── Risk lanes ──────────────────────────────────────────────────────────── */

/**
 * Which group of work a row belongs to. A row is in EXACTLY ONE lane — a row
 * with a wage difference and a registration-number difference is payroll work,
 * full stop, and must never also appear under "administrative only" where a
 * later wave's bulk action could sweep the wage change along with it.
 */
export type ReconciliationLane =
  | "payroll"
  | "administrative"
  | "evidence"
  | "missingHere"
  | "conclusion"
  | "blocked"
  | "matched";

/** Display order: most consequential first, collapsed plain matches last. */
export const RECONCILIATION_LANES = [
  "payroll",
  "administrative",
  "evidence",
  "missingHere",
  "conclusion",
  "blocked",
  "matched",
] as const satisfies readonly ReconciliationLane[];

/**
 * Precedence is NOT the display order, and every place they differ is
 * deliberate:
 *
 * 1. INVALID is tested FIRST even though it renders last: the line could not be
 *    read, so there is no employee to act on and nothing to conclude about.
 * 2. `stale` blocks — but only the MUTATING work. A stale row has had its
 *    employee edited since the comparison, so an acceptance on it would write
 *    against facts that have moved and the server refuses it. A CONCLUSION
 *    writes nothing: it records a judgement about a line, changes no
 *    enrollment, no employee and no payroll, and the server's own completion
 *    rule asks only that such rows be concluded. So `conclusion` is tested
 *    BEFORE `stale` — filing a stale MISSING_THERE under "blocked work" demands
 *    a fresh workbook for a review the server considers completable, which is a
 *    dead end we invented. The row still renders as stale; both facts are shown.
 * 3. The classification checks come BEFORE the diff checks, because a
 *    MISSING_HERE row legitimately carries a full set of diffs (every fact GOSI
 *    holds against the nothing we hold) and several are payroll-impacting.
 *    Creating an enrollment needs the complete fact set, so that row is a
 *    whole-row decision — it must not be shredded into the payroll lane's
 *    per-field ones.
 */
export function reconciliationLane(row: ReconciliationRowView): ReconciliationLane {
  if (row.classification === "INVALID") return "blocked";
  if (row.classification === "MISSING_THERE" || row.classification === "UNMATCHED") {
    return "conclusion";
  }
  if (row.stale) return "blocked";
  if (row.classification === "MISSING_HERE") return "missingHere";
  if (row.diffs.some((d) => d.materiality === "PAYROLL_IMPACTING")) return "payroll";
  if (row.diffs.length > 0) return "administrative";
  // Agrees with the file and still can't be paid — the cheapest wins on the
  // readiness radar, which is exactly why they are never folded into `matched`.
  if (row.blockedReasons.length > 0) return "evidence";
  return "matched";
}

/** Group rows by lane, preserving server order inside each. */
export function partitionReconciliationLanes(
  rows: readonly ReconciliationRowView[],
): Record<ReconciliationLane, ReconciliationRowView[]> {
  const out = {
    payroll: [],
    administrative: [],
    evidence: [],
    missingHere: [],
    conclusion: [],
    blocked: [],
    matched: [],
  } as Record<ReconciliationLane, ReconciliationRowView[]>;
  for (const row of rows) out[reconciliationLane(row)].push(row);
  return out;
}

/**
 * What a diff side actually says.
 *
 * `null` and the literal `"NONE"` are DIFFERENT claims and rendering them the
 * same way misstates a money field: `null` is "this side holds nothing to
 * compare", `"NONE"` is the sentinel the workbook uses to say "clear what you
 * hold" — an instruction, not an absence. On `gosiWageOverride` the first means
 * "payroll computes the wage" and the second means "stop overriding it".
 */
export type ReconciliationValueKind = "value" | "absent" | "cleared";

export function reconciliationValueKind(value: string | null | undefined): ReconciliationValueKind {
  if (value === null || value === undefined) return "absent";
  if (value === "NONE") return "cleared";
  return "value";
}

/* ── Decisions: what may still be decided, and by whom ───────────────────── */

/**
 * A difference carries its own decision, and `null` means pending.
 *
 * A decision is TERMINAL EXACTLY ONCE — the server has no notion of a
 * superseded decision, and re-deciding is a `422 ALREADY_DECIDED`. So a decided
 * difference is never offered again anywhere in this module: not in a control,
 * not in a bulk sweep, not in a submitted item.
 *
 * `stale` is a SEPARATE fact and stays one. A row can be decided AND stale at
 * the same time; both are shown, and neither is relabelled as the other.
 */
export function isDiffPending(diff: ReconciliationFieldDiffView): boolean {
  return diff.decision === null || diff.decision === undefined;
}

export function pendingDiffs(row: ReconciliationRowView): ReconciliationFieldDiffView[] {
  return row.diffs.filter(isDiffPending);
}

/**
 * A MATCHES row's acceptance is ROW-level (`evidenceAcceptance`) — it is the
 * decision that discharges a row which agrees with the file and still cannot
 * compute money. It is deliberately not a difference: there is none to decide.
 */
export function isEvidencePending(row: ReconciliationRowView): boolean {
  return row.evidenceAcceptance === null || row.evidenceAcceptance === undefined;
}

/**
 * The lanes a bulk action may sweep. Everything else is decided one field at a
 * time — the payroll lane most of all, which is the whole point of the
 * partition: no bulk action may ever carry a contribution change along with a
 * record-keeping one.
 */
export const BULK_SELECTABLE_LANES = [
  "administrative",
  "evidence",
] as const satisfies readonly ReconciliationLane[];

export function isLaneBulkSelectable(lane: ReconciliationLane): boolean {
  return (BULK_SELECTABLE_LANES as readonly ReconciliationLane[]).includes(lane);
}

/**
 * Has somebody ALREADY recorded a decision on this row, in an earlier command?
 *
 * Submitting the rest of the row is still allowed, and deliberately so: a
 * review only reaches `APPLIED` when every difference is decided, so refusing
 * to finish a row that was half-decided last Tuesday would leave that review
 * permanently uncompletable — and a decision is terminal, so the earlier one
 * cannot be taken back either.
 *
 * What it must NOT do is stay quiet. A second acceptance opens a SECOND
 * effective-dated window on that employee, days apart from the first, and the
 * consent sheet has to say so before the click rather than after.
 */
export function hasEarlierDecision(row: ReconciliationRowView): boolean {
  if (row.evidenceAcceptance !== null && row.evidenceAcceptance !== undefined) return true;
  return row.diffs.some((d) => !isDiffPending(d));
}

/** Is there anything left on this row that a decision could be recorded about? */
export function isRowDecidable(row: ReconciliationRowView): boolean {
  const lane = reconciliationLane(row);
  // Blocked rows can take no decision the server would accept; conclusion rows
  // need a disposition (wave 4), not an acceptance; a plain match needs nothing.
  if (lane === "blocked" || lane === "conclusion" || lane === "matched") return false;
  if (lane === "evidence") return isEvidencePending(row);
  return pendingDiffs(row).length > 0;
}

/**
 * May a bulk action include this row?
 *
 * The lane check already excludes anything payroll-impacting, because
 * `reconciliationLane` files a row with ANY payroll-impacting difference under
 * `payroll`. The second test is deliberate belt-and-braces: it re-reads the
 * pending differences themselves, so a future change to the lane rule cannot
 * quietly make a wage change bulk-selectable.
 */
export function isRowBulkSelectable(row: ReconciliationRowView): boolean {
  if (!isRowDecidable(row)) return false;
  if (!isLaneBulkSelectable(reconciliationLane(row))) return false;
  return !pendingDiffs(row).some((d) => d.materiality === "PAYROLL_IMPACTING");
}

/** One pending difference's decision as the admin has drafted it, pre-submit. */
export interface DiffDecisionDraft {
  decision: AcceptDecision;
  /** Required when refusing a payroll-impacting difference — the contract says so. */
  note?: string;
}

/** Keyed by `fieldDiffId`, or by `rowId` for a row-level evidence acceptance. */
export type ReconciliationDraft = Readonly<Record<string, DiffDecisionDraft | undefined>>;

/** Refusing a contribution change is a judgement, so the contract requires a note. */
export function noteRequired(diff: ReconciliationFieldDiffView, decision: AcceptDecision): boolean {
  return decision === "KEEP_OURS" && diff.materiality === "PAYROLL_IMPACTING";
}

/**
 * THE ROW IS THE MINIMUM SUBMISSION UNIT.
 *
 * The field is the decision unit — each difference is accepted or refused on
 * its own — but a row goes in whole or not at all. Submitting one field today
 * and another on Thursday opens two effective-dated windows days apart on one
 * employee, which is a mess nobody asked for and nobody can see coming.
 *
 * Already-decided differences are not part of the test: they are not being
 * submitted, so they cannot hold the row back.
 */
export function isRowSubmissionReady(
  row: ReconciliationRowView,
  draft: ReconciliationDraft,
): boolean {
  if (!isRowDecidable(row)) return false;
  const pending = pendingDiffs(row);
  // No differences at all — this is the evidence acceptance, keyed by row id.
  if (pending.length === 0) return draft[row.id] !== undefined;
  return pending.every((d) => {
    const drafted = draft[d.id];
    if (drafted === undefined) return false;
    if (!noteRequired(d, drafted.decision)) return true;
    return (drafted.note ?? "").trim().length > 0;
  });
}

/** The server's cap on one command (contract `maxItems`). */
export const ACCEPT_MAX_ITEMS = 200;

function acceptItem(
  rowId: string,
  expectedRowRevision: number,
  decision: AcceptDecision,
  extras: { fieldDiffId?: string; effectiveFrom?: string; note?: string },
): AcceptDecisionItem {
  const note = extras.note?.trim();
  return {
    rowId,
    expectedRowRevision,
    decision,
    ...(extras.fieldDiffId !== undefined ? { fieldDiffId: extras.fieldDiffId } : {}),
    ...(extras.effectiveFrom !== undefined && extras.effectiveFrom.length > 0
      ? { effectiveFrom: extras.effectiveFrom }
      : {}),
    ...(note !== undefined && note.length > 0 ? { note } : {}),
  };
}

/**
 * The command body's items, built from whole rows only.
 *
 * `effectiveFrom` is serialized IDENTICALLY on every item of one row — one row,
 * one window, one date. A row with per-item dates would open several windows on
 * one employee out of a single click.
 */
export function buildAcceptItems(
  rows: readonly ReconciliationRowView[],
  draft: ReconciliationDraft,
  effectiveFrom: Readonly<Record<string, string | undefined>> = {},
): AcceptDecisionItem[] {
  const items: AcceptDecisionItem[] = [];
  for (const row of rows) {
    if (!isRowSubmissionReady(row, draft)) continue;
    const date = effectiveFrom[row.id];
    const pending = pendingDiffs(row);
    if (pending.length === 0) {
      const drafted = draft[row.id];
      if (drafted === undefined) continue;
      items.push(
        acceptItem(row.id, row.rowRevision, drafted.decision, {
          ...(date !== undefined ? { effectiveFrom: date } : {}),
          ...(drafted.note !== undefined ? { note: drafted.note } : {}),
        }),
      );
      continue;
    }
    for (const diff of pending) {
      const drafted = draft[diff.id];
      if (drafted === undefined) continue;
      items.push(
        acceptItem(row.id, row.rowRevision, drafted.decision, {
          fieldDiffId: diff.id,
          ...(date !== undefined ? { effectiveFrom: date } : {}),
          ...(drafted.note !== undefined ? { note: drafted.note } : {}),
        }),
      );
    }
  }
  return items;
}

/* ── What one row's command actually said ────────────────────────────────── */

/** What was decided about one employee, counted off the command itself. */
export interface RowDecisionSummary {
  /**
   * No difference to decide — the acceptance records the workbook as evidence.
   * Nothing about our data changes, so nothing about our data may be claimed.
   */
  evidenceOnly: boolean;
  accepted: number;
  kept: number;
}

/**
 * What this row's decisions SAY, read from the command rather than inferred
 * from the response.
 *
 * The sheet and the result panel both frame an employee from this, so the last
 * thing the admin reads cannot contradict the thing they consented to.
 */
export function summarizeRowDecisions(
  row: ReconciliationRowView | undefined,
  draft: ReconciliationDraft,
): RowDecisionSummary {
  if (row === undefined) return { evidenceOnly: false, accepted: 0, kept: 0 };
  const pending = pendingDiffs(row);
  if (pending.length === 0) return { evidenceOnly: true, accepted: 0, kept: 0 };
  let accepted = 0;
  let kept = 0;
  for (const d of pending) {
    const decision = draft[d.id]?.decision;
    if (decision === "ACCEPT_THEIRS") accepted += 1;
    if (decision === "KEEP_OURS") kept += 1;
  }
  return { evidenceOnly: false, accepted, kept };
}

/**
 * Did this acceptance open a new enrollment period?
 *
 * `effectiveFrom` is the ONLY field that answers this, and it answers it
 * exactly: CC-411 defines it as the date the window this opened takes effect,
 * and null precisely when it opened none. Two wrong answers were tried first,
 * and they failed in opposite directions:
 *
 *   - `enrollmentId != null` OVERSTATED. It is the enrollment the row now
 *     points at, so an evidence acceptance and a refusal on somebody we already
 *     hold both come back carrying the pre-existing one. Every acceptance
 *     reported that a period had been opened.
 *   - "a value was taken" UNDERSTATED. Opening a window and changing a value
 *     are INDEPENDENT: an evidence acceptance that verifies an enrollment opens
 *     a window without changing any value, and suppressing the clause there
 *     denied something that had visibly happened — the in-force line directly
 *     beneath it named the day the window opened.
 */
export function openedEnrollmentWindow(row: { effectiveFrom?: string | null }): boolean {
  return row.effectiveFrom !== null && row.effectiveFrom !== undefined;
}

/* ── Per-row timing on an accepted row ───────────────────────────────────── */

/**
 * Are this row's four CC-408 timing fields real?
 *
 * They are trustworthy for any command THIS client issues: the four fields
 * deserialize as `false`/`null` only when an idempotency key is replayed
 * against an acceptance committed BEFORE CC-408, and no accept call of ours has
 * ever been sent to any environment — the flow is being written now. Reaching
 * that state would take a key collision with a different client's pre-deploy
 * acceptance, and our key is a content digest of the command.
 *
 * If that ever stops being true — somebody backfills acceptances, or another
 * client shares our key space — THIS is the guard that catches it: the stale
 * shape is all four absent while the row is verified with nothing blocking,
 * which a live planner can never produce (verified + unblocked + in force is
 * payroll-executable by definition, and a window not yet in force always names
 * the day it opens).
 */
export function hasRowTiming(row: AcceptedRowResult): boolean {
  const absent =
    row.effectiveNow === false &&
    (row.becomesEffectiveOn === null || row.becomesEffectiveOn === undefined) &&
    row.payrollExecutableNow === false &&
    (row.becomesPayrollExecutableOn === null || row.becomesPayrollExecutableOn === undefined);
  return !(absent && row.verified && row.blockedReasons.length === 0);
}

/**
 * What to say about ONE accepted row's timing — the row's own answer where it
 * has one, the job's all-rows aggregate where it structurally does not.
 *
 * Falling back to the aggregate is deliberately the vaguer sentence, and it is
 * only reached in the case that already looks wrong.
 */
export function resolveAcceptedTiming(
  row: AcceptedRowResult,
  jobEffectiveNow: boolean,
): {
  effectiveNow: boolean;
  payrollExecutableNow: boolean;
  becomesPayrollExecutableOn: string | null;
} {
  if (hasRowTiming(row)) {
    return {
      effectiveNow: row.effectiveNow,
      payrollExecutableNow: row.payrollExecutableNow,
      becomesPayrollExecutableOn: row.becomesPayrollExecutableOn ?? null,
    };
  }
  const unblocked = row.blockedReasons.length === 0;
  // `effectiveFrom` is null exactly when NO window opened — an evidence-only
  // acceptance, or a MISSING_HERE row whose every answer was refused (CC-411).
  // Such a row has no date to become executable on, and inventing one is what
  // produced the contradiction that fix was for. Say nothing instead.
  const opensOn =
    typeof row.effectiveFrom === "string" && row.effectiveFrom !== "" ? row.effectiveFrom : null;
  return {
    effectiveNow: jobEffectiveNow,
    payrollExecutableNow: jobEffectiveNow && unblocked,
    becomesPayrollExecutableOn: !jobEffectiveNow && unblocked ? opensOn : null,
  };
}

/* ── Idempotency ─────────────────────────────────────────────────────────── */

/**
 * A stable digest of the exact command. ANY edit the server would see — a
 * flipped decision, a changed note, a moved date, a row added or dropped —
 * changes it; re-ordering does not, because a reordered list is the same
 * command.
 *
 * JSON, NOT a delimiter join. `note` is free text up to 500 characters, so a
 * note carrying whatever separator we picked could impersonate the structure
 * around it and make two DIFFERENT commands digest identically — at which point
 * `nextAcceptKey` reuses a spent key on a changed body. JSON escaping is the
 * boundary user text cannot cross.
 */
export function acceptCommandFingerprint(items: readonly AcceptDecisionItem[]): string {
  return JSON.stringify(
    items
      .map((i) =>
        JSON.stringify([
          i.rowId,
          i.expectedRowRevision,
          i.fieldDiffId ?? null,
          i.decision,
          i.effectiveFrom ?? null,
          i.note ?? null,
        ]),
      )
      // Sorted on the SERIALIZED tuple, so ordering is normalized without a
      // second delimiter scheme to get wrong.
      .sort(),
  );
}

export interface AcceptCommandKey {
  fingerprint: string;
  key: string;
}

/**
 * The idempotency key's whole life in one function.
 *
 * Minted when the payload is FROZEN — at preflight — and reused for an
 * identical retry, which is what makes a network retry safe: the server answers
 * a replay from the stored result. ANY edit mints a new one, because replaying
 * a key with a different body is a `409 IDEMPOTENCY_CONFLICT`, and — worse —
 * reusing it after a commit would return the OLD result as if it were this
 * command's.
 */
export function nextAcceptKey(
  previous: AcceptCommandKey | null,
  fingerprint: string,
  mint: () => string = () => globalThis.crypto.randomUUID(),
): AcceptCommandKey {
  if (previous !== null && previous.fingerprint === fingerprint) return previous;
  return { fingerprint, key: mint() };
}

/* ── Conclusions: the rows an acceptance can never discharge ─────────────── */

export type DispositionItem = components["schemas"]["DispositionItem"];
export type DispositionView = components["schemas"]["DispositionView"];
export type DispositionedRowResult = components["schemas"]["DispositionedRowResult"];
export type RecordDispositionsResponse = components["schemas"]["RecordDispositionsResponse"];
export type DispositionReason = DispositionItem["reasonCode"];

export const DISPOSITION_REASONS = [
  "CONFIRMED_NOT_REGISTERED",
  "OUT_OF_SCOPE_FILE",
  "FILE_ROW_ERROR",
  "EMPLOYEE_NOT_IN_LINK_HR",
] as const satisfies readonly DispositionReason[];

/**
 * APPLICABILITY IS NOT SYMMETRIC, and the server enforces it.
 *
 * `CONFIRMED_NOT_REGISTERED` is a statement about an employee we hold and GOSI
 * does not, so it can only be true of a `MISSING_THERE` row.
 * `EMPLOYEE_NOT_IN_LINK_HR` is the mirror claim and can only be true of an
 * `UNMATCHED` one. The other two are about the FILE, so either row can carry
 * them.
 *
 * The UI offers only what can be true of the row it is on. The server's 422 is
 * the backstop, never the gate: a control that offers a reason the command will
 * be refused for is a control that asks the admin to guess.
 */
const MISSING_THERE_REASONS = [
  "CONFIRMED_NOT_REGISTERED",
  "OUT_OF_SCOPE_FILE",
  "FILE_ROW_ERROR",
] as const satisfies readonly DispositionReason[];

const UNMATCHED_REASONS = [
  "EMPLOYEE_NOT_IN_LINK_HR",
  "OUT_OF_SCOPE_FILE",
  "FILE_ROW_ERROR",
] as const satisfies readonly DispositionReason[];

export function dispositionReasonsFor(
  classification: ReconciliationClassification,
): readonly DispositionReason[] {
  if (classification === "MISSING_THERE") return MISSING_THERE_REASONS;
  if (classification === "UNMATCHED") return UNMATCHED_REASONS;
  // Every other classification is decided by accepting or refusing its
  // differences. A conclusion on one is not "not offered yet" — it is not a
  // thing that exists.
  return [];
}

/** `null` is undecided. A conclusion is terminal; it is never offered again. */
export function isDispositionPending(row: ReconciliationRowView): boolean {
  return row.disposition === null || row.disposition === undefined;
}

/**
 * Is a conclusion something this row can still take?
 *
 * The lane does the work — and note what it deliberately does NOT exclude: a
 * STALE row. A conclusion mutates nothing, so an edited employee does not
 * invalidate a judgement about the line, and the server's completion rule asks
 * only that these rows be concluded. Excluding them demanded a fresh workbook
 * for a review the server considers finishable.
 */
export function isRowDispositionable(row: ReconciliationRowView): boolean {
  return reconciliationLane(row) === "conclusion" && isDispositionPending(row);
}

/** One conclusion as the admin has drafted it, pre-submit. */
export interface DispositionDraft {
  reasonCode: DispositionReason;
  /** REQUIRED and non-blank — the contract says so, and deliberately. */
  note: string;
}

/** Keyed by `rowId`: a conclusion is about the whole line, never a field. */
export type DispositionDraftMap = Readonly<Record<string, DispositionDraft | undefined>>;

/**
 * A drafted conclusion the server would accept: a reason that can be true of
 * this row, and a note with something in it.
 *
 * The note is not validation theatre. It is what makes this a judgement rather
 * than a dismiss button, and it is the only thing anybody reading this review in
 * a year will have to go on.
 */
export function isDispositionReady(
  row: ReconciliationRowView,
  draft: DispositionDraftMap,
): boolean {
  if (!isRowDispositionable(row)) return false;
  const drafted = draft[row.id];
  if (drafted === undefined) return false;
  if (!dispositionReasonsFor(row.classification).includes(drafted.reasonCode)) return false;
  const note = drafted.note.trim();
  return note.length > 0 && note.length <= DISPOSITION_NOTE_MAX;
}

/** The server's cap on one conclusions command (contract `maxItems`). */
export const DISPOSITION_MAX_ITEMS = 500;

/**
 * The contract's `maxLength` on a note. Enforced here as well as on the input,
 * so a paste that outruns the field cannot be submitted into a 400.
 */
export const DISPOSITION_NOTE_MAX = 500;

export function buildDispositionItems(
  rows: readonly ReconciliationRowView[],
  draft: DispositionDraftMap,
): DispositionItem[] {
  const items: DispositionItem[] = [];
  for (const row of rows) {
    if (!isDispositionReady(row, draft)) continue;
    const drafted = draft[row.id];
    if (drafted === undefined) continue;
    items.push({
      rowId: row.id,
      expectedRowRevision: row.rowRevision,
      reasonCode: drafted.reasonCode,
      note: drafted.note.trim(),
    });
  }
  return items;
}

/**
 * The conclusions command's digest — same rule as `acceptCommandFingerprint`
 * and for the same reason: `note` is free text, so JSON escaping is the
 * boundary it cannot cross to impersonate the structure around it.
 */
export function dispositionCommandFingerprint(items: readonly DispositionItem[]): string {
  return JSON.stringify(
    items.map((i) => JSON.stringify([i.rowId, i.expectedRowRevision, i.reasonCode, i.note])).sort(),
  );
}

/* ── What is holding this review open ────────────────────────────────────── */

/**
 * Why the review is not complete, counted in EMPLOYEES rather than decisions —
 * the row is the unit the admin works in, and it is the unit every control on
 * the screen is attached to.
 *
 * Each row lands in exactly ONE bucket, because the lane partition already
 * decided which piece of work it is. A stale row carrying undecided differences
 * is stale work, not decision work: no decision on it can be submitted, so
 * counting it as "still to decide" would name an action that does not exist.
 */
export interface ReviewCompletionBlockers {
  /** Differences nobody has accepted or refused yet. */
  undecided: number;
  /** Agrees with GOSI, still unverified, and no evidence acceptance recorded. */
  unevidenced: number;
  /** `MISSING_THERE` / `UNMATCHED` with no conclusion recorded. */
  unconcluded: number;
  /** Edited since the comparison. The exit is a fresh export, not a decision. */
  stale: number;
  /** Unreadable lines. Not dismissable by design. */
  invalid: number;
  /** Rows counted in any bucket above. Zero means the review can complete. */
  total: number;
}

export function reviewCompletionBlockers(
  rows: readonly ReconciliationRowView[],
): ReviewCompletionBlockers {
  const out = { undecided: 0, unevidenced: 0, unconcluded: 0, stale: 0, invalid: 0, total: 0 };
  for (const row of rows) {
    switch (reconciliationLane(row)) {
      case "blocked":
        if (row.classification === "INVALID") out.invalid += 1;
        else out.stale += 1;
        break;
      case "conclusion":
        if (isDispositionPending(row)) out.unconcluded += 1;
        break;
      case "evidence":
        if (isEvidencePending(row)) out.unevidenced += 1;
        break;
      case "payroll":
      case "administrative":
      case "missingHere":
        if (pendingDiffs(row).length > 0) out.undecided += 1;
        break;
      case "matched":
        break;
    }
  }
  out.total = out.undecided + out.unevidenced + out.unconcluded + out.stale + out.invalid;
  return out;
}

/* ── Refusals ────────────────────────────────────────────────────────────── */

export const PREFLIGHT_STALE_REASONS = [
  "TOKEN_INVALID",
  "TOKEN_NOT_FOR_THIS_COMMAND",
  "TOKEN_EXPIRED",
  "PLAN_CHANGED",
] as const;
export type PreflightStaleReason = (typeof PREFLIGHT_STALE_REASONS)[number];

export const ROW_STALE_REASONS = [
  "ROW_REVISION",
  "WINDOW_SUPERSEDED",
  "ENROLLMENT_APPEARED",
  "WINDOW_ENDS_BEFORE",
] as const;
export type RowStaleReason = (typeof ROW_STALE_REASONS)[number];

const PREFLIGHT_STALE_SET = new Set<string>(PREFLIGHT_STALE_REASONS);
const ROW_STALE_SET = new Set<string>(ROW_STALE_REASONS);

/**
 * What the server refused, and — separately — what the admin can do about it.
 *
 * Only one of these is an ERROR in the sense of "something went wrong":
 * `unknown`. `preflight_stale` in particular is not a failure at all, it is the
 * world having moved between the preview and the commit, and the recovery is to
 * ask again — never to discard what the admin decided.
 */
export type AcceptRefusal =
  | { kind: "preflight_stale"; reason: PreflightStaleReason | null }
  | { kind: "busy" }
  | {
      kind: "already_decided";
      rowId: string | null;
      fieldDiffId: string | null;
      decision: AcceptDecision | null;
    }
  | { kind: "accept_rejected"; reason: string | null }
  | {
      kind: "effective_from_refused";
      rowId: string | null;
      minEffectiveFrom: string | null;
      suggestedEffectiveFrom: string | null;
      requestedEffectiveFrom: string | null;
    }
  | { kind: "row_stale"; reason: RowStaleReason | null; rowId: string | null }
  | { kind: "idempotency_conflict" }
  | { kind: "unknown" };

/** What the UI must offer next. One recovery per refusal, never a generic retry. */
export type AcceptRecovery =
  /** Re-run preflight with the SAME decisions and show what changed. */
  | "repreflight"
  /** Genuinely transient — a plain "try again" is the right button. */
  | "retry"
  /** Somebody else moved the row; re-read it before offering anything. */
  | "refresh"
  /** The date is the problem; the admin picks again and re-confirms. */
  | "repick_date"
  /** This key is spent on a different body; start the command over. */
  | "restart"
  /** Nothing to offer beyond the server's own words. */
  | "none";

function str(bag: Record<string, unknown>, key: string): string | null {
  const v = bag[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

function argsOf(err: unknown): Record<string, unknown> {
  if (!isRecord(err)) return {};
  const extensions = err["extensions"];
  if (!isRecord(extensions)) return {};
  const args = extensions["args"];
  // `args` is the documented home (be-726: named keys). Reading the extensions
  // bag itself as a fallback costs nothing and survives a flatter payload.
  return isRecord(args) ? args : extensions;
}

/**
 * Classify an accept/preflight refusal. Matches on the FULL error code — every
 * one of these is `GOSI_RECONCILIATION_`-prefixed and a suffix match would
 * collide with the enrollment codes.
 *
 * Values are validated, never cast: a reason outside the closed set becomes
 * `null` so the UI falls back to the server's pre-localized `title` rather than
 * inventing a recovery from a value it does not understand.
 */
export function classifyAcceptRefusal(err: unknown): AcceptRefusal {
  if (!isRecord(err)) return { kind: "unknown" };
  const code = err["errorCode"];
  const args = argsOf(err);
  const reason = str(args, "reason");

  switch (code) {
    case "GOSI_RECONCILIATION_PREFLIGHT_STALE":
      return {
        kind: "preflight_stale",
        reason:
          reason !== null && PREFLIGHT_STALE_SET.has(reason)
            ? (reason as PreflightStaleReason)
            : null,
      };
    case "GOSI_RECONCILIATION_PREFLIGHT_BUSY":
    case "GOSI_RECONCILIATION_PAYROLL_IN_FLIGHT":
      return { kind: "busy" };
    case "GOSI_RECONCILIATION_ACCEPT_REJECTED": {
      if (reason === "ALREADY_DECIDED") {
        const decision = str(args, "decision");
        return {
          kind: "already_decided",
          rowId: str(args, "rowId"),
          fieldDiffId: str(args, "fieldDiffId"),
          decision: decision === "ACCEPT_THEIRS" || decision === "KEEP_OURS" ? decision : null,
        };
      }
      return { kind: "accept_rejected", reason };
    }
    case "GOSI_RECONCILIATION_EFFECTIVE_FROM_REFUSED":
      return {
        kind: "effective_from_refused",
        rowId: str(args, "rowId"),
        minEffectiveFrom: str(args, "minEffectiveFrom"),
        suggestedEffectiveFrom: str(args, "suggestedEffectiveFrom"),
        requestedEffectiveFrom: str(args, "requestedEffectiveFrom"),
      };
    case "GOSI_RECONCILIATION_ROW_STALE":
      return {
        kind: "row_stale",
        reason: reason !== null && ROW_STALE_SET.has(reason) ? (reason as RowStaleReason) : null,
        rowId: str(args, "rowId"),
      };
    case "GOSI_RECONCILIATION_IDEMPOTENCY_CONFLICT":
      return { kind: "idempotency_conflict" };
    default:
      return { kind: "unknown" };
  }
}

export function acceptRecovery(refusal: AcceptRefusal): AcceptRecovery {
  switch (refusal.kind) {
    case "preflight_stale":
      return "repreflight";
    case "busy":
      return "retry";
    case "already_decided":
    case "row_stale":
      return "refresh";
    case "effective_from_refused":
      return "repick_date";
    case "idempotency_conflict":
      return "restart";
    case "accept_rejected":
    case "unknown":
      return "none";
  }
}

/* ── Conclusion and supersede refusals (CC-412) ──────────────────────────── */

export type DispositionRejectedReason =
  components["schemas"]["ReconciliationDispositionRejectedArgs"]["reason"];
export type SupersedeRejectedReason =
  components["schemas"]["ReconciliationSupersedeRejectedArgs"]["reason"];

export const DISPOSITION_REJECTED_REASONS = [
  "JOB_NOT_REVIEWABLE",
  "DUPLICATE_ROW",
  "ROW_NOT_DISPOSITIONABLE",
  "ALREADY_DISPOSITIONED",
  "REASON_NOT_APPLICABLE",
] as const satisfies readonly DispositionRejectedReason[];

export const SUPERSEDE_REJECTED_REASONS = [
  "SAME_JOB",
  "ALREADY_SUPERSEDED",
  "NOT_REVIEWABLE",
  "SUPERSEDING_JOB_NOT_FOUND",
  "SUPERSEDING_NOT_REVIEWABLE",
  "SUPERSEDING_NOT_NEWER",
] as const satisfies readonly SupersedeRejectedReason[];

const DISPOSITION_REJECTED_SET = new Set<string>(DISPOSITION_REJECTED_REASONS);
const SUPERSEDE_REJECTED_SET = new Set<string>(SUPERSEDE_REJECTED_REASONS);

/**
 * What the server refused a conclusions command for.
 *
 * Every reason is validated against the published closed set rather than cast:
 * a value outside it becomes `unknown`, so the UI falls back to the server's own
 * pre-localized `title` instead of inventing a recovery from a token it does not
 * understand. `rowId` rides along on every reason except `JOB_NOT_REVIEWABLE` —
 * which is about the review, not any row in it — so a refusal can point at the
 * line that caused it rather than failing the batch abstractly.
 */
export type DispositionRefusal =
  | { kind: "rejected"; reason: DispositionRejectedReason; rowId: string | null }
  | { kind: "row_stale"; reason: RowStaleReason | null; rowId: string | null }
  | { kind: "idempotency_conflict" }
  | { kind: "unknown" };

export function classifyDispositionRefusal(err: unknown): DispositionRefusal {
  if (!isRecord(err)) return { kind: "unknown" };
  const args = argsOf(err);
  const reason = str(args, "reason");

  switch (err["errorCode"]) {
    case "GOSI_RECONCILIATION_DISPOSITION_REJECTED":
      if (reason === null || !DISPOSITION_REJECTED_SET.has(reason)) return { kind: "unknown" };
      return {
        kind: "rejected",
        reason: reason as DispositionRejectedReason,
        rowId: str(args, "rowId"),
      };
    case "GOSI_RECONCILIATION_ROW_STALE":
      return {
        kind: "row_stale",
        reason: reason !== null && ROW_STALE_SET.has(reason) ? (reason as RowStaleReason) : null,
        rowId: str(args, "rowId"),
      };
    case "GOSI_RECONCILIATION_IDEMPOTENCY_CONFLICT":
      return { kind: "idempotency_conflict" };
    default:
      return { kind: "unknown" };
  }
}

export function dispositionRecovery(refusal: DispositionRefusal): AcceptRecovery {
  switch (refusal.kind) {
    case "row_stale":
      return "refresh";
    case "idempotency_conflict":
      return "restart";
    case "rejected":
      // All four row-shaped reasons mean the comparison we are holding is out
      // of date with the server's — including the two the UI is supposed to
      // have made unreachable. Re-reading is the only honest next move.
      // DUPLICATE_ROW is the exception: it is a malformed command, and the
      // draft is keyed by row id so it cannot be produced from this screen.
      return refusal.reason === "DUPLICATE_ROW" ? "none" : "refresh";
    case "unknown":
      return "none";
  }
}

/**
 * Does this refusal mean the REVIEW moved on rather than one row in it?
 *
 * The only reason that carries no `rowId`, and the only one whose recovery has
 * to re-read the job: `reviewStatus` is what the write controls hang off, so
 * refetching the rows alone leaves them up and loops the same refusal.
 */
export function isReviewClosedRefusal(refusal: DispositionRefusal): boolean {
  return refusal.kind === "rejected" && refusal.reason === "JOB_NOT_REVIEWABLE";
}

/**
 * What the server refused a handover for.
 *
 * The published set spells the review's own not-open reason `NOT_REVIEWABLE`,
 * with no prefix — `JOB_NOT_REVIEWABLE` belongs to the DISPOSITION vocabulary
 * and never appears here. Branching on the wrong one is a branch that can never
 * fire, which is why both sets are validated against the generated types.
 */
export type SupersedeRefusal =
  | { kind: "rejected"; reason: SupersedeRejectedReason }
  | { kind: "unknown" };

export function classifySupersedeRefusal(err: unknown): SupersedeRefusal {
  if (!isRecord(err)) return { kind: "unknown" };
  if (err["errorCode"] !== "GOSI_RECONCILIATION_SUPERSEDE_REJECTED") return { kind: "unknown" };
  const reason = str(argsOf(err), "reason");
  if (reason === null || !SUPERSEDE_REJECTED_SET.has(reason)) return { kind: "unknown" };
  return { kind: "rejected", reason: reason as SupersedeRejectedReason };
}

/**
 * What to offer after a refused handover — and the distinction that matters:
 *
 * The three `SUPERSEDING_*` reasons are about the review TAKING OVER, not this
 * one. The successor is the problem, so the answer is to pick a different one —
 * the picker stays open with that candidate struck off. Treating them like the
 * others would send the admin away from a screen where one more click was the
 * whole fix.
 */
export type SupersedeRecovery = "pick_another" | "refresh" | "none";

export function supersedeRecovery(refusal: SupersedeRefusal): SupersedeRecovery {
  if (refusal.kind === "unknown") return "none";
  switch (refusal.reason) {
    case "SUPERSEDING_JOB_NOT_FOUND":
    case "SUPERSEDING_NOT_REVIEWABLE":
    case "SUPERSEDING_NOT_NEWER":
      return "pick_another";
    case "ALREADY_SUPERSEDED":
    case "NOT_REVIEWABLE":
      // This review moved on under us; re-read it and the controls follow.
      return "refresh";
    case "SAME_JOB":
      // Unreachable from the picker, which excludes this review from its own
      // candidates. Nothing to offer beyond the server's words.
      return "none";
  }
}

/* ── 422 rejection payload ───────────────────────────────────────────────── */

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

function asIssue(x: unknown): ReconciliationFileIssue | null {
  if (!isRecord(x)) return null;
  const reason = x["reason"];
  if (typeof reason !== "string" || !REASON_SET.has(reason)) return null;
  // Only `reason` and `detail` are required (CC-411). The three locators are
  // null for a problem that genuinely has no location — a macro project belongs
  // to the workbook, not to a cell — so they stay null rather than becoming a
  // sheet named "" on row 0, which is a place that does not exist.
  return {
    reason: reason as RejectionReason,
    sheet: typeof x["sheet"] === "string" && x["sheet"].length > 0 ? x["sheet"] : null,
    row:
      typeof x["row"] === "number" && Number.isFinite(x["row"]) && x["row"] > 0 ? x["row"] : null,
    column: typeof x["column"] === "string" && x["column"].length > 0 ? x["column"] : null,
    detail: typeof x["detail"] === "string" ? x["detail"] : "",
  };
}

/** One nameable piece of "where in the workbook this is". */
export interface FileIssueLocationPart {
  part: "sheet" | "row" | "column";
  /** Already a string: a row is a LOCATOR that pairs with Excel's own gutter. */
  value: string;
}

/**
 * Where an issue is, as the pieces that actually exist — sheet, line, column,
 * in that order, each dropped when the server has none.
 *
 * All three are nullable and legitimately so: a macro project has no sheet, a
 * missing sheet has no column, a hidden row has no column. An empty array is
 * the honest answer for a whole-file problem, and the caller renders NOTHING
 * for it — not a dash, not an empty bracket, and never a printed "null".
 */
export function fileIssueLocation(issue: ReconciliationFileIssue): FileIssueLocationPart[] {
  const parts: FileIssueLocationPart[] = [];
  if (typeof issue.sheet === "string" && issue.sheet.length > 0) {
    parts.push({ part: "sheet", value: issue.sheet });
  }
  if (typeof issue.row === "number" && issue.row > 0) {
    parts.push({ part: "row", value: String(issue.row) });
  }
  if (typeof issue.column === "string" && issue.column.length > 0) {
    parts.push({ part: "column", value: issue.column });
  }
  return parts;
}

/**
 * The rejection as this client is willing to state it. Identical to the
 * contract's args except that the two schema versions may be `null` — see
 * `parseFileRejection`, which drops them rather than the whole panel.
 */
export type ParsedFileRejection = Omit<
  ReconciliationRejectionArgs,
  "expectedSchemaVersion" | "detectedSchemaVersion"
> & {
  expectedSchemaVersion: number | null;
  detectedSchemaVersion: number | null;
};

/**
 * Lift the `GOSI_RECONCILIATION_FILE_REJECTED` args off a thrown ApiError.
 *
 * Returns null when the LOAD-BEARING members do not validate, so the caller
 * falls back to the server's own pre-localized `title`. The shape is
 * re-validated rather than cast: the args ride in `extensions` as untyped
 * RFC 7807 members.
 *
 * FAIL CLOSED ON WHAT IS LOAD-BEARING; DEGRADE ON WHAT IS DECORATIVE. The
 * distinction is what the admin is told to DO:
 *   - a false `truncated` tells them "these are all the problems" when they are
 *     not, so they fix the listed ones and get refused again;
 *   - a false `reExportRequired` tells them "just correct this file" when the
 *     workbook is stale and can never be accepted, however carefully they edit;
 *   - `reason`, `failureClass`, `issues` and `issueCount` are the refusal
 *     itself. Guessing any of these is worse than showing no panel.
 *
 * The two schema versions and `detail` are DESCRIPTIVE: absent, they cost one
 * sentence. And the lesson worth keeping is that the first version of this
 * function failed closed on them too — the live backend never sends
 * `detectedSchemaVersion` despite the contract marking it required, so EVERY
 * rejected upload fell through to the bare one-line banner and this whole panel
 * — reason, issue list, exit — was unreachable in production. Failing closed on
 * a decorative field cost the entire screen.
 */
export function parseFileRejection(err: unknown): ParsedFileRejection | null {
  if (!isRecord(err)) return null;
  if (err["errorCode"] !== "GOSI_RECONCILIATION_FILE_REJECTED") return null;
  const extensions = err["extensions"];
  if (!isRecord(extensions)) return null;
  const args = extensions["args"];
  if (!isRecord(args)) return null;

  const reason = args["reason"];
  const failureClass = args["failureClass"];
  const detail = args["detail"];
  const issueCount = args["issueCount"];
  const truncated = args["truncated"];
  const expectedSchemaVersion = args["expectedSchemaVersion"];
  const detectedSchemaVersion = args["detectedSchemaVersion"];
  const reExportRequired = args["reExportRequired"];

  if (typeof reason !== "string" || !REASON_SET.has(reason)) return null;
  if (typeof failureClass !== "string" || !CLASS_SET.has(failureClass)) return null;
  if (typeof issueCount !== "number" || !Number.isFinite(issueCount)) return null;
  if (typeof truncated !== "boolean") return null;
  if (typeof reExportRequired !== "boolean") return null;
  if (!Array.isArray(args["issues"])) return null;

  // Individual malformed issues are dropped rather than failing the whole
  // panel — `truncated` and `issueCount` still tell the truth about the list
  // being incomplete, which is the claim that has to stay honest.
  const issues = args["issues"]
    .map(asIssue)
    .filter((i): i is ReconciliationFileIssue => i !== null);

  return {
    reason: reason as RejectionReason,
    failureClass: failureClass as FailureClass,
    // Descriptive prose. Absent, the panel simply says less.
    detail: typeof detail === "string" ? detail : "",
    issues,
    // The server's count, not the array length: `truncated` exists precisely
    // because the two legitimately disagree.
    issueCount,
    truncated,
    expectedSchemaVersion: schemaVersion(expectedSchemaVersion),
    detectedSchemaVersion: schemaVersion(detectedSchemaVersion),
    // Server-provided, never derived from the reason enum.
    reExportRequired,
  };
}

/** A schema version we can print, or `null` — which drops one sentence. */
function schemaVersion(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
}

/* ── Wire ────────────────────────────────────────────────────────────────── */

type AnyGet = {
  GET: (
    path: string,
    init?: { parseAs?: "json" | "text" | "blob" | "arrayBuffer" | "stream" },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};
type AnyPost = {
  POST: (
    path: string,
    init: { body: unknown; bodySerializer?: (b: unknown) => BodyInit },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

const TEMPLATE_PATH = "/api/v1/tenants/me/insurance-enrollments/template";
const RECONCILIATION_PATH = "/api/v1/tenants/me/insurance-enrollments/reconciliation";

/**
 * `GET .../insurance-enrollments/template` — the pre-filled workbook.
 *
 * Fetched as a blob through the api-client so the Bearer token travels; a plain
 * `<a download>` bypasses the auth middleware and 401s.
 */
export function useDownloadReconciliationTemplate(_slug: string): () => Promise<void> {
  const client = useApiClient();
  return useCallback(async () => {
    const res = await (client as unknown as AnyGet).GET(TEMPLATE_PATH, { parseAs: "blob" });
    if (res.error) throw res.error as ApiError;
    const blob = res.data as Blob | undefined;
    if (!blob) throw new Error("Empty template response");
    triggerBlobDownload(blob, "gosi-reconciliation-template.xlsx");
  }, [client]);
}

/**
 * `POST .../insurance-enrollments/reconciliation` — 202 with the job id.
 *
 * Multipart, field name `file`. Content-Type is left unset on purpose: the
 * browser has to write the multipart boundary itself.
 */
export function useUploadReconciliation(
  slug: string,
): UseMutationResult<{ jobId: string }, ApiError, { file: File }> {
  const client = useApiClient();
  return useTenantMutation<{ file: File }, { jobId: string }>(client, {
    mutationKey: [...qk.insuranceEnrollments.reconciliation.all(slug), "upload"],
    mutationFn: async (c: ApiClient, vars) => {
      const form = new FormData();
      form.append("file", vars.file);
      const res = await (c as unknown as AnyPost).POST(RECONCILIATION_PATH, {
        body: form as unknown,
        bodySerializer: (b) => b as BodyInit,
      });
      if (res.error) throw res.error as ApiError;
      return res.data as { jobId: string };
    },
  });
}

/**
 * `GET .../insurance-enrollments/reconciliation/{jobId}` — polled while the
 * comparison runs.
 *
 * Polling stops on a terminal processingStatus AND at the ceiling, so a job
 * that never finishes leaves a stopped screen with a refresh button rather than
 * a request every two seconds for as long as the tab is open.
 */
export function useReconciliationJob(
  slug: string,
  jobId: string | null,
  opts: { enabled?: boolean } = {},
): UseQueryResult<ReconciliationJobView, ApiError> & { phase: ReconciliationPhase } {
  const client = useApiClient();
  const qc = useQueryClient();
  const hasJob = typeof jobId === "string" && jobId.length > 0;
  const enabled = hasJob && opts.enabled !== false;
  const queryKey = qk.insuranceEnrollments.reconciliation.job(slug, jobId ?? "__none__");
  const query = useTenantQuery<ReconciliationJobView>(client, {
    queryKey,
    enabled,
    // `dataUpdateCount` is the read count, which is what the ceiling falls back
    // to when the job's own `uploadedAt` clock is unreadable.
    refetchInterval: (q) => reconciliationPollInterval(q.state),
    queryFn: async (c) => {
      const res = await (c as unknown as AnyGet).GET(
        `${RECONCILIATION_PATH}/${encodeURIComponent(jobId ?? "")}`,
      );
      if (res.error) throw res.error as ApiError;
      return res.data as ReconciliationJobView;
    },
  });

  // The phase the POLLER decided, handed to the UI so a stopped poll and a
  // spinning screen can never disagree. Read from the same query state the
  // `refetchInterval` callback sees.
  const polls = qc.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  return { ...query, phase: reconciliationPhase(query.data, Date.now(), polls) };
}

/** The server's cap. Asking for more is a 400, so the client clamps instead. */
export const RECONCILIATION_ROWS_MAX_SIZE = 200;
export const RECONCILIATION_ROWS_DEFAULT_SIZE = 50;

/**
 * `GET .../reconciliation/{jobId}/rows` — the comparison, line by line.
 *
 * Rows that already match AND can compute money are omitted unless
 * `includeMatched` is set: they are the ones with nothing to do, and paging
 * through a thousand of them to find the twelve that need attention is the
 * failure this default exists to prevent.
 */
export function useReconciliationRows(
  slug: string,
  jobId: string | null,
  opts: {
    page?: number;
    size?: number;
    includeMatched?: boolean;
    enabled?: boolean;
  } = {},
): UseQueryResult<PagedReconciliationRows, ApiError> {
  const client = useApiClient();
  const page = Math.max(0, opts.page ?? 0);
  const size = Math.min(
    RECONCILIATION_ROWS_MAX_SIZE,
    Math.max(1, opts.size ?? RECONCILIATION_ROWS_DEFAULT_SIZE),
  );
  const includeMatched = opts.includeMatched === true;
  const hasJob = typeof jobId === "string" && jobId.length > 0;

  return useTenantQuery<PagedReconciliationRows>(client, {
    queryKey: [
      ...qk.insuranceEnrollments.reconciliation.rows(slug, jobId ?? "__none__"),
      page,
      size,
      includeMatched,
    ],
    enabled: hasJob && opts.enabled !== false,
    queryFn: async (c) => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (includeMatched) params.set("include", "matched");
      const res = await (c as unknown as AnyGet).GET(
        `${RECONCILIATION_PATH}/${encodeURIComponent(jobId ?? "")}/rows?${params.toString()}`,
      );
      if (res.error) throw res.error as ApiError;
      return res.data as PagedReconciliationRows;
    },
  });
}

/* ── Resuming a review ───────────────────────────────────────────────────── */

/** Which reviews are still someone's work to finish. */
export const RESUMABLE_REVIEW_STATUSES = ["OPEN", "PARTIALLY_APPLIED"] as const;

/**
 * Which reviews may REPLACE another one.
 *
 * Wider than the resumable set on purpose: the server accepts any completed,
 * still-current review of this account uploaded no earlier than the one being
 * replaced — `APPLIED` included. A review that was uploaded, decided in full and
 * is now the current truth is the BEST successor there is, and leaving it out
 * told the admin no successor existed while one sat in the list.
 */
export const SUPERSEDING_REVIEW_STATUSES = [
  "OPEN",
  "PARTIALLY_APPLIED",
  "APPLIED",
] as const satisfies readonly ReconciliationReviewStatus[];

const OPEN_JOBS_PAGE_SIZE = 20;

/**
 * `GET .../reconciliation?reviewStatus=OPEN,PARTIALLY_APPLIED` — the open
 * reviews, newest first.
 *
 * The entry point to this screen used to be browser history: an admin who
 * uploaded a workbook and closed the tab had no way back to the review. FAILED
 * and still-processing jobs are excluded by the server structurally, so
 * everything this returns is a review somebody can actually resume — and the
 * resumer is frequently NOT the uploader, which is why the list carries
 * `uploadedBy` and `uploadedAt`.
 */
export function useOpenReconciliationJobs(
  slug: string,
  opts: {
    enabled?: boolean;
    cursor?: string;
    /** Defaults to the resumable set; supersede asks for a wider one. */
    statuses?: readonly ReconciliationReviewStatus[];
  } = {},
): UseQueryResult<ReconciliationJobPage, ApiError> {
  const client = useApiClient();
  const cursor = opts.cursor ?? "";
  const statuses = opts.statuses ?? RESUMABLE_REVIEW_STATUSES;
  const reviewStatus = statuses.join(",");
  return useTenantQuery<ReconciliationJobPage>(client, {
    // The status filter is part of the KEY: the resume list and the supersede
    // picker ask for different sets, and sharing one cache entry would serve
    // whichever answered first to both.
    queryKey: [...qk.insuranceEnrollments.reconciliation.openJobs(slug), cursor, reviewStatus],
    enabled: opts.enabled !== false,
    queryFn: async (c) => {
      const params = new URLSearchParams({
        reviewStatus,
        size: String(OPEN_JOBS_PAGE_SIZE),
      });
      // A blank cursor is the FIRST page, and the contract says omit it —
      // sending `cursor=` is a value the keyset decoder has to reject.
      if (cursor.length > 0) params.set("cursor", cursor);
      const res = await (c as unknown as AnyGet).GET(`${RECONCILIATION_PATH}?${params.toString()}`);
      if (res.error) throw res.error as ApiError;
      return res.data as ReconciliationJobPage;
    },
  });
}

/* ── Accept: preview, then commit ────────────────────────────────────────── */

export interface AcceptCommand {
  idempotencyKey: string;
  items: AcceptDecisionItem[];
  /**
   * The employees these rows belong to. NOT sent — the contract has no field
   * for it — and used only to invalidate their per-employee enrollment reads
   * after a commit. The response names rows, and a row id cannot be resolved to
   * an employee without the comparison the caller is already holding.
   */
  employeeIds?: readonly string[];
}

/**
 * `POST .../{jobId}/accept/preflight` — WRITES NOTHING.
 *
 * Runs the same planner as the commit, under the same locks, and seals a digest
 * of that plan into `preflightToken`. So every sentence the consent sheet says
 * about an employee comes from here — what would be decided, whether they
 * become payroll-executable, from when, and which payroll runs get re-queued.
 * None of it may be guessed from the request, because the request does not know
 * about a finalized run or an enrollment that appeared this morning.
 *
 * `preflightToken` is `required` on the shared request schema and ignored on
 * this route — this call is what ISSUES it — so an empty string is sent.
 */
export function useAcceptPreflight(
  slug: string,
  jobId: string,
): UseMutationResult<AcceptPreflightResponse, ApiError, AcceptCommand> {
  const client = useApiClient();
  return useTenantMutation<AcceptCommand, AcceptPreflightResponse>(client, {
    mutationKey: [...qk.insuranceEnrollments.reconciliation.job(slug, jobId), "accept-preflight"],
    mutationFn: async (c, vars) => {
      const res = await (c as unknown as AnyPost).POST(
        `${RECONCILIATION_PATH}/${encodeURIComponent(jobId)}/accept/preflight`,
        { body: { ...vars, preflightToken: "" } },
      );
      if (res.error) throw res.error as ApiError;
      return res.data as AcceptPreflightResponse;
    },
  });
}

/**
 * `POST .../{jobId}/accept` — the same body plus the token the preflight issued.
 *
 * All-or-nothing per command. The RESULT per row is what actually happened —
 * `verified`, `blockedReasons`, `payrollExecutableNow` — and a 200 says only
 * that the command was applied, never that anybody can be paid.
 *
 * Everything downstream is re-read rather than patched from the response: the
 * JOB is authoritative for where the review now stands, and the readiness radar
 * is authoritative for who can be paid today.
 */
export function useAcceptReconciliation(
  slug: string,
  jobId: string,
): UseMutationResult<
  AcceptReconciliationResponse,
  ApiError,
  AcceptCommand & { preflightToken: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useTenantMutation<
    AcceptCommand & { preflightToken: string },
    AcceptReconciliationResponse
  >(client, {
    mutationKey: [...qk.insuranceEnrollments.reconciliation.job(slug, jobId), "accept"],
    mutationFn: async (c, vars) => {
      const res = await (c as unknown as AnyPost).POST(
        `${RECONCILIATION_PATH}/${encodeURIComponent(jobId)}/accept`,
        // Only the three fields the contract carries — `employeeIds` is ours,
        // for cache invalidation, and an unknown member on a money command is
        // exactly the kind of thing a strict deserializer rejects.
        {
          body: {
            idempotencyKey: vars.idempotencyKey,
            preflightToken: vars.preflightToken,
            items: vars.items,
          },
        },
      );
      if (res.error) throw res.error as ApiError;
      return res.data as AcceptReconciliationResponse;
    },
    onSuccess: (_data, vars) => {
      // The job read, not the mutation response, is authoritative for the
      // review's current status — and an acceptance moves the readiness radar
      // for the whole tenant, not only these rows.
      void qc.invalidateQueries({
        queryKey: qk.insuranceEnrollments.reconciliation.all(slug),
      });
      void qc.invalidateQueries({ queryKey: qk.insuranceEnrollments.tenantAll(slug) });
      // An acceptance CLOSES one enrollment window and OPENS another. The
      // per-employee reads live under their own prefix, so an employee profile
      // open in another tab would otherwise keep showing the window this
      // command just closed. Same "both views of the same fact" rule as
      // `invalidateEnrollments` in the enrollment hooks.
      for (const employeeId of vars.employeeIds ?? []) {
        void qc.invalidateQueries({ queryKey: qk.insuranceEnrollments.all(slug, employeeId) });
      }
    },
  });
}

/* ── Conclusions and supersede ───────────────────────────────────────────── */

/**
 * `POST .../{jobId}/dispositions` — records what a row that cannot be applied
 * MEANS.
 *
 * Shaped like an acceptance — an idempotency key, quoted row revisions, all of
 * it or none — and unlike one in the way that matters: it changes no enrollment,
 * no employee and no payroll. So only the reconciliation reads are invalidated;
 * the readiness radar and the per-employee enrollment reads cannot have moved.
 *
 * The response carries the recomputed `reviewStatus`, which is where a fully
 * decided review becomes APPLIED — but the JOB read stays the authority for
 * where the review stands, same as the accept path.
 */
export function useRecordDispositions(
  slug: string,
  jobId: string,
): UseMutationResult<
  RecordDispositionsResponse,
  ApiError,
  { idempotencyKey: string; items: DispositionItem[] }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useTenantMutation<
    { idempotencyKey: string; items: DispositionItem[] },
    RecordDispositionsResponse
  >(client, {
    mutationKey: [...qk.insuranceEnrollments.reconciliation.job(slug, jobId), "dispositions"],
    mutationFn: async (c, vars) => {
      const res = await (c as unknown as AnyPost).POST(
        `${RECONCILIATION_PATH}/${encodeURIComponent(jobId)}/dispositions`,
        { body: { idempotencyKey: vars.idempotencyKey, items: vars.items } },
      );
      if (res.error) throw res.error as ApiError;
      return res.data as RecordDispositionsResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.insuranceEnrollments.reconciliation.all(slug) });
    },
  });
}

/**
 * `POST .../{jobId}/supersede` — this review is replaced by a newer one.
 *
 * NEVER AUTOMATIC. A second upload existing is not a reason to abandon the
 * first review, which may be half applied — so this is only ever an explicit
 * act, and it keeps everything: the acceptances, conclusions and evidence on
 * the superseded review stay readable, because they record what somebody
 * decided and that stays true whichever review is current.
 *
 * Idempotent by its own terminal state: superseding again by the SAME job
 * returns the same answer; by a different one it is refused.
 */
export function useSupersedeReconciliation(
  slug: string,
  jobId: string,
): UseMutationResult<ReconciliationJobView, ApiError, { supersedingJobId: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useTenantMutation<{ supersedingJobId: string }, ReconciliationJobView>(client, {
    mutationKey: [...qk.insuranceEnrollments.reconciliation.job(slug, jobId), "supersede"],
    mutationFn: async (c, vars) => {
      const res = await (c as unknown as AnyPost).POST(
        `${RECONCILIATION_PATH}/${encodeURIComponent(jobId)}/supersede`,
        { body: { supersedingJobId: vars.supersedingJobId } },
      );
      if (res.error) throw res.error as ApiError;
      return res.data as ReconciliationJobView;
    },
    onSuccess: () => {
      // Both jobs move: this one becomes SUPERSEDED, and the open-review list
      // must stop offering it as somebody's outstanding work.
      void qc.invalidateQueries({ queryKey: qk.insuranceEnrollments.reconciliation.all(slug) });
    },
  });
}
